/** Assessment, advisory draft and evidence share one event workspace. */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import CveLink from '../../components/CveLink';
import Banner from '../../components/Banner';
import Chip from '../../components/Chip';
import Modal from '../../components/Modal';
import Spinner from '../../components/Spinner';
import { errorMessage, submitBewertung } from '../../lib/api';
import type {
  BewertungInput,
  BewertungResult,
  TriageDocumentSummary,
  VulnerabilityEventRow,
} from '../../lib/types';

import { EventCveFacts, EventInstanzen, EventScopeFacts } from './EventFacts';
import { BEWERTUNG_LABELS, BEWERTUNG_TONE, komponenteLabel } from './eventHelpers';
import { AdvisoryPanel, AttachmentsPanel } from './TriageDocumentPanels';
import { useTriageDocuments } from './useTriageDocuments';
import { useTriageNavigationGuard } from './useTriageNavigationGuard';
import { deriveAssessmentReason } from './advisoryTemplate';

export type AssessmentTab = 'assessment' | 'attachments';

export interface BewertungDialogProps {
  event: VulnerabilityEventRow;
  bewerter: string;
  canWrite?: boolean;
  initialTab?: AssessmentTab;
  onClose: () => void;
  onSaved: (result: BewertungResult) => void;
  onDocumentsChanged: (eventId: number, summary: TriageDocumentSummary) => void;
}

export function BewertungDialog({
  event,
  bewerter,
  canWrite = true,
  initialTab = 'assessment',
  onClose,
  onSaved,
  onDocumentsChanged,
}: BewertungDialogProps): ReactNode {
  const [pending, setPending] = useState<BewertungInput | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<AssessmentTab>(initialTab);
  const [confirmClose, setConfirmClose] = useState(false);
  const documents = useTriageDocuments(event.id, event.begruendung ?? '');
  const begruendung = useMemo(() => deriveAssessmentReason(documents.content), [documents.content]);
  const saveLock = useRef(false);
  const closeConfirmation = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const busy = pending !== null || documents.busy;
  const unsaved = canWrite && (documents.dirty || documents.queue.length > 0);
  const saveDisabled =
    busy ||
    documents.loading ||
    documents.loadError !== null ||
    documents.queue.length > 0 ||
    !begruendung;
  const navigation = useTriageNavigationGuard(unsaved || busy);

  useEffect(() => {
    if (!documents.documents) return;
    onDocumentsChanged(event.id, {
      advisory: Boolean(documents.documents.advisory.content.trim()),
      attachments: documents.documents.attachments.length,
    });
  }, [documents.documents, event.id, onDocumentsChanged]);

  useEffect(() => {
    if (!unsaved && !busy) return;
    function warnBeforeUnload(e: BeforeUnloadEvent): void {
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [unsaved, busy]);

  useEffect(() => {
    if (confirmClose) closeConfirmation.current?.focus();
  }, [confirmClose]);

  useEffect(() => {
    if (navigation.state === 'blocked' && !busy) setConfirmClose(true);
  }, [navigation.state, busy]);

  function continueEditing(): void {
    if (navigation.state === 'blocked') navigation.reset();
    setConfirmClose(false);
    window.requestAnimationFrame(() => {
      tabsRef.current?.querySelector<HTMLButtonElement>('[aria-selected="true"]')?.focus();
    });
  }

  function discardChanges(): void {
    if (busy) return;
    if (navigation.state === 'blocked') navigation.proceed();
    else onClose();
  }

  function requestClose(): void {
    if (busy) return;
    if (unsaved) setConfirmClose(true);
    else onClose();
  }

  async function save(bewertung: BewertungInput): Promise<void> {
    if (!canWrite || saveDisabled || saveLock.current) return;
    saveLock.current = true;
    setPending(bewertung);
    setError(null);
    try {
      // A failed verdict leaves the already persisted draft available for retry.
      await documents.save();
      const response = await submitBewertung(event.id, {
        bewertung,
        begruendung,
        bewerter,
      });
      onSaved(response.result);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      saveLock.current = false;
      setPending(null);
    }
  }

  const tabs: Array<{ id: AssessmentTab; label: string; count?: number }> = [
    { id: 'assessment', label: 'Bewertung & Advisory' },
    { id: 'attachments', label: 'Anhänge', count: documents.documents?.attachments.length ?? 0 },
  ];

  return (
    <Modal
      title={
        <>
          Triage <span className="muted">/</span> <CveLink cve={event.cve.id} />
        </>
      }
      subtitle={`Event #${event.id} · ${komponenteLabel(event)}`}
      onClose={requestClose}
      size="wide"
      dismissable={!busy}
      footer={
        <>
          {confirmClose ? (
            <div
              className="triage-close-confirm"
              role="alert"
              tabIndex={-1}
              ref={closeConfirmation}
            >
              <div>
                <strong>Ungespeicherte Änderungen verwerfen?</strong>
                <p>Bereits gespeicherte Entwürfe und Anhänge bleiben erhalten.</p>
              </div>
              <button type="button" className="ghost" onClick={continueEditing} disabled={busy}>
                Weiter bearbeiten
              </button>
              <button type="button" className="danger" onClick={discardChanges} disabled={busy}>
                Änderungen verwerfen
              </button>
            </div>
          ) : (
            <>
              <button type="button" className="ghost" onClick={requestClose} disabled={busy}>
                Schließen
              </button>
              <span className="triage-footer-note">
                {busy
                  ? 'Wird gespeichert…'
                  : !canWrite
                    ? 'Gespeicherte Dokumente zum Event'
                    : !begruendung && canWrite
                      ? 'Vorlage ausfüllen, um die Bewertung zu speichern'
                      : 'Vorlagentext wird als Begründung übernommen'}
              </span>
              <div className="spacer" />
              {canWrite && (
                <>
                  <button
                    type="button"
                    className="ghost"
                    onClick={() => void save('NICHT_BETROFFEN')}
                    disabled={saveDisabled}
                    title={
                      !begruendung
                        ? 'Bitte die Begründung im Vorlagentext ergänzen'
                        : 'CVE ist auf unsere Verwendung der Komponente nicht anwendbar'
                    }
                  >
                    {pending === 'NICHT_BETROFFEN' && <Spinner />}Nicht betroffen
                  </button>
                  <button
                    type="button"
                    className="danger"
                    onClick={() => void save('BETROFFEN')}
                    disabled={saveDisabled}
                    title={
                      !begruendung
                        ? 'Bitte die Begründung im Vorlagentext ergänzen'
                        : 'Bewertung speichern und Benachrichtigungen für betroffene Kunden anlegen'
                    }
                  >
                    {pending === 'BETROFFEN' && <Spinner variant="on-accent" />}Betroffen
                  </button>
                </>
              )}
            </>
          )}
        </>
      }
    >
      <div className="triage-workspace" inert={confirmClose}>
        <div className="triage-context-bar">
          <span>Aktuelle Bewertung</span>
          <Chip tone={BEWERTUNG_TONE[event.bewertung]}>{BEWERTUNG_LABELS[event.bewertung]}</Chip>
        </div>
        <div
          className="tabs triage-workspace-tabs"
          role="tablist"
          aria-label="Triage-Bereiche"
          ref={tabsRef}
          onKeyDown={(e) => {
            const index = tabs.findIndex((item) => item.id === tab);
            let next: number;
            if (e.key === 'ArrowRight') next = (index + 1) % tabs.length;
            else if (e.key === 'ArrowLeft') next = (index + tabs.length - 1) % tabs.length;
            else if (e.key === 'Home') next = 0;
            else if (e.key === 'End') next = tabs.length - 1;
            else return;
            e.preventDefault();
            const selected = tabs[next];
            if (selected) {
              setTab(selected.id);
              tabsRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus();
            }
          }}
        >
          {tabs.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              id={`triage-tab-${item.id}`}
              aria-controls={`triage-panel-${item.id}`}
              aria-selected={tab === item.id}
              tabIndex={tab === item.id ? 0 : -1}
              onClick={() => setTab(item.id)}
            >
              {item.label}
              {item.count !== undefined && <Chip>{item.count}</Chip>}
              {item.id === 'assessment' && canWrite && documents.dirty && (
                <span className="triage-unsaved-dot" aria-label="Ungespeicherte Änderungen" />
              )}
            </button>
          ))}
        </div>

        {error && (
          <Banner
            kind="error"
            sub="Die Bewertung wurde nicht gespeichert, bereits gespeicherte Dokumente bleiben erhalten."
            onDismiss={() => setError(null)}
          >
            {error}
          </Banner>
        )}
        {documents.error && !error && <Banner kind="error">{documents.error}</Banner>}
        {documents.loadError && (
          <Banner
            kind="error"
            sub={
              <button
                type="button"
                className="ghost"
                onClick={() => {
                  void documents.load();
                }}
              >
                Erneut laden
              </button>
            }
          >
            Dokumente konnten nicht geladen werden: {documents.loadError}
          </Banner>
        )}
        {documents.queue.some((item) => item.status === 'failed') && (
          <Banner
            kind="warn"
            sub="Bitte fehlgeschlagene Uploads unter „Anhänge“ erneut versuchen oder verwerfen, bevor Sie die Bewertung speichern."
          >
            Einige Anhänge wurden nicht gespeichert.
          </Banner>
        )}

        <div
          role="tabpanel"
          id="triage-panel-assessment"
          aria-labelledby="triage-tab-assessment"
          hidden={tab !== 'assessment'}
          tabIndex={0}
        >
          <section className="triage-doc-panel">
            <div className="triage-panel-heading">
              <h3>Anwendbarkeit bewerten</h3>
            </div>
            <div className="triage-assessment-summary">
              <div>
                <span>Komponente</span>
                <strong>{event.komponente.name ?? 'Unbekannt'}</strong>
                <span>
                  {event.komponente.version ?? 'Version unbekannt'} ·{' '}
                  {event.komponente.hersteller ?? 'Hersteller unbekannt'}
                </span>
              </div>
              <div>
                <span>Betroffener Umfang</span>
                <strong>
                  {event.betroffene.length} Produktinstanz
                  {event.betroffene.length === 1 ? '' : 'en'}
                </strong>
                <span>
                  {event.betroffene_kunden} Kunde{event.betroffene_kunden === 1 ? '' : 'n'} ·{' '}
                  {event.produktversionen.length} Produktversion
                  {event.produktversionen.length === 1 ? '' : 'en'}
                </span>
              </div>
            </div>
            {documents.loading ? (
              <p role="status">
                <Spinner /> Vorlage wird geladen…
              </p>
            ) : (
              documents.documents && (
                <AdvisoryPanel
                  event={event}
                  state={documents}
                  disabled={busy}
                  canWrite={canWrite}
                />
              )
            )}
            {event.begruendung && event.begruendung.trim() !== begruendung && (
              <details className="triage-context-details">
                <summary>Bisherige Bewertungsbegründung</summary>
                <p className="triage-quote">{event.begruendung}</p>
              </details>
            )}
            <div className="triage-assessor">
              <span className="triage-assessor-avatar" aria-hidden="true">
                {bewerter.slice(0, 1).toUpperCase()}
              </span>
              <div>
                <span className="hint">{canWrite ? 'Bewertet von' : 'Angemeldet als'}</span>
                <strong>{bewerter}</strong>
              </div>
            </div>
            <details className="triage-context-details">
              <summary>Schwachstelle & betroffenen Umfang prüfen</summary>
              <EventCveFacts event={event} />
              <EventScopeFacts event={event} />
              <EventInstanzen event={event} />
            </details>
          </section>
        </div>
        <div
          role="tabpanel"
          id="triage-panel-attachments"
          aria-labelledby="triage-tab-attachments"
          hidden={tab !== 'attachments'}
          tabIndex={0}
        >
          {documents.loading ? (
            <p role="status">
              <Spinner /> Anhänge werden geladen…
            </p>
          ) : (
            <AttachmentsPanel event={event} state={documents} disabled={busy} canWrite={canWrite} />
          )}
        </div>
      </div>
    </Modal>
  );
}

export default BewertungDialog;
